import { useState } from 'react';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Input,
  LoadingSkeleton,
  Select,
  Textarea,
} from '@nexora/design-system';
import {
  useClosingChecklist,
  useClosingPeriod,
  useClosingPeriods,
  useValidateSign,
} from './useClosing';
import {
  STATUS_PERIODO_LABEL,
  type FechamentoPeriodoStatus,
} from './types';
import { formatCount, formatDateTime } from '../../shared/utils/format';
import { useAuth } from '../../shared/auth/AuthContext';

/* ═══════════════════════════════════════════════════════════════════════════
   Validação Final e Assinatura do Fechamento
   ───────────────────────────────────────────────────────────────────────────
   Contrato consumido (rota real /api/v1/closing):

   GET  /closing/periods                    → Paginated<FechamentoPeriodo>
     (seletor de período)

   GET  /closing/periods/:id                → FechamentoPeriodo
     { id, label, status, masterPeriodLock, criticalPendingIssues,
       pendingCriticalCount, assinadoPor, cargoAssinante, assinadoEm,
       fechadoPor, fechadoEm, notaValidacao }

   GET  /closing/periods/:id/checklist      → Paginated<ChecklistItem>
     (usado para o resumo de conclusão: quantos itens faltam antes de assinar)

   POST /closing/periods/:id/validate-sign  → FechamentoPeriodo
     { assinadoPor, cargoAssinante?, notaValidacao? } — valida o workflow e
     assina/fecha o período (assinadoPor default = usuário da sessão).

   Fluxo da tela: resumo do período (status, assinatura, pendências críticas,
   conclusão do checklist) + confirmação com nome/cargo/nota → validate-sign.
   O botão fica bloqueado enquanto o checklist não estiver 100% concluído.
   ═══════════════════════════════════════════════════════════════════════════ */

const PERIODO_BADGE_VARIANT: Record<
  FechamentoPeriodoStatus,
  'success' | 'warning' | 'error' | 'info' | 'neutral'
> = {
  ABERTO: 'info',
  EM_ANDAMENTO: 'warning',
  CONCILIACAO: 'warning',
  VALIDACAO: 'info',
  FECHADO: 'success',
  CANCELADO: 'error',
};

export function ClosingValidateSignPage(): JSX.Element {
  const { user } = useAuth();
  const [periodId, setPeriodId] = useState<string | undefined>(undefined);
  const [assinadoPor, setAssinadoPor] = useState(() => user?.name ?? '');
  const [cargoAssinante, setCargoAssinante] = useState('');
  const [notaValidacao, setNotaValidacao] = useState('');

  const periodsQuery = useClosingPeriods({ limit: 50 });
  const periods = periodsQuery.data?.data ?? [];
  const activePeriodId = periodId ?? periods[0]?.id;

  const periodQuery = useClosingPeriod(activePeriodId);
  const checklistQuery = useClosingChecklist(activePeriodId, { limit: 100 });
  const signMutation = useValidateSign(activePeriodId);

  const period = periodQuery.data;
  const items = checklistQuery.data?.data ?? [];
  const concluidos = items.filter((i) => i.status === 'CONCLUIDO' || i.status === 'NAO_APLICAVEL').length;
  const checklistCompleto = items.length > 0 && concluidos === items.length;
  const jaAssinado = !!period?.assinadoEm;
  const bloqueado =
    !period ||
    period.status === 'FECHADO' ||
    period.status === 'CANCELADO' ||
    jaAssinado ||
    signMutation.isPending ||
    (items.length > 0 && !checklistCompleto);

  const issuesNaoResolvidas = (period?.criticalPendingIssues ?? []).filter((i) => !i.resolvido);

  const handleSign = async (): Promise<void> => {
    if (!assinadoPor.trim()) {
      window.alert('Informe o nome de quem assina o fechamento.');
      return;
    }
    try {
      await signMutation.mutateAsync({
        assinadoPor: assinadoPor.trim(),
        cargoAssinante: cargoAssinante.trim() || undefined,
        notaValidacao: notaValidacao.trim() || undefined,
      });
      window.alert('Fechamento validado e assinado com sucesso.');
      periodQuery.refetch();
      checklistQuery.refetch();
    } catch (err) {
      window.alert(err instanceof Error ? err.message : 'Erro ao validar e assinar o fechamento.');
    }
  };

  return (
    <div className="closing-validate-page">
      {/* Cabeçalho */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-end gap-md mb-lg">
        <div>
          <p className="font-label-sm text-label-sm text-on-surface-variant mb-1 uppercase tracking-wider">
            Fechamento Mensal / Finalização
          </p>
          <h1 className="font-display-lg text-display-lg text-primary m-0">Validação Final e Assinatura</h1>
          <p className="font-body-md text-body-md text-on-surface-variant mt-xs">
            Confira o resumo do período e assine para encerrar o fechamento.
          </p>
        </div>
        <div style={{ flex: '0 0 280px' }}>
          <Select
            label="Período de fechamento"
            value={activePeriodId ?? ''}
            onChange={(e) => setPeriodId(e.target.value)}
            selectSize="sm"
            placeholder="Selecione um período"
          >
            {periods.map((p) => (
              <option key={p.id} value={p.id}>
                {p.label}
              </option>
            ))}
          </Select>
        </div>
      </div>

      {/* Erro */}
      {periodQuery.error && (
        <Card variant="outlined" padding="1.5rem" className="mb-lg">
          <p className="m-0" style={{ fontWeight: 600, color: '#ba1a1a' }}>Erro ao carregar o período</p>
          <p className="m-0" style={{ fontSize: '0.875rem', color: '#74777f', marginTop: '0.25rem' }}>
            {periodQuery.error instanceof Error ? periodQuery.error.message : 'Erro desconhecido'}
          </p>
          <Button variant="primary" size="sm" onClick={() => periodQuery.refetch()} style={{ marginTop: '0.75rem' }}>
            Tentar novamente
          </Button>
        </Card>
      )}

      {periodQuery.isLoading && (
        <div style={{ marginBottom: '1rem' }}>
          <LoadingSkeleton count={3} height="96px" borderRadius="12px" />
        </div>
      )}

      {!periodQuery.isLoading && !periodQuery.error && (
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-lg">
          {/* Resumo do período */}
          <div className="lg:col-span-7 space-y-lg">
            <Card variant="outlined" padding="lg">
              <CardHeader className="p-0 border-0">
                <div className="flex items-center gap-md">
                  <h3 className="font-headline-md text-headline-md text-on-surface m-0">
                    {period?.label ?? 'Período'}
                  </h3>
                  {period && (
                    <Badge variant={PERIODO_BADGE_VARIANT[period.status]} size="md" dot>
                      {STATUS_PERIODO_LABEL[period.status]}
                    </Badge>
                  )}
                </div>
              </CardHeader>
              <div className="grid grid-cols-2 gap-md" style={{ marginTop: '1rem' }}>
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  <div>Conclusão do checklist</div>
                  <div className="font-headline-sm text-headline-sm text-on-surface" style={{ fontWeight: 700 }}>
                    {checklistQuery.isLoading ? '—' : `${concluidos}/${items.length} itens`}
                  </div>
                </div>
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  <div>Pendências críticas</div>
                  <div className="font-headline-sm text-headline-sm" style={{ fontWeight: 700, color: issuesNaoResolvidas.length > 0 ? '#ba1a1a' : '#2e7d32' }}>
                    {formatCount(issuesNaoResolvidas.length)}
                  </div>
                </div>
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  <div>Assinado por</div>
                  <div className="font-label-md text-label-md text-on-surface">{period?.assinadoPor ?? '—'}</div>
                  {period?.cargoAssinante && (
                    <div className="font-label-sm text-label-sm text-on-surface-variant">{period.cargoAssinante}</div>
                  )}
                </div>
                <div className="font-body-sm text-body-sm text-on-surface-variant">
                  <div>Assinado em</div>
                  <div className="font-label-md text-label-md text-on-surface">
                    {period?.assinadoEm ? formatDateTime(period.assinadoEm) : '—'}
                  </div>
                </div>
              </div>
              {period?.fechadoPor && period?.fechadoEm && (
                <p className="font-label-sm text-label-sm text-on-surface-variant m-0" style={{ marginTop: '0.75rem' }}>
                  Fechado por {period.fechadoPor} em {formatDateTime(period.fechadoEm)}
                </p>
              )}
              {period?.notaValidacao && (
                <p className="font-body-sm text-body-sm text-on-surface-variant m-0" style={{ marginTop: '0.5rem' }}>
                  Nota de validação: {period.notaValidacao}
                </p>
              )}
            </Card>

            {issuesNaoResolvidas.length > 0 && (
              <Card variant="outlined" padding="lg">
                <h3 className="font-headline-sm text-headline-sm" style={{ margin: 0, color: '#93000a' }}>
                  Pendências críticas não resolvidas
                </h3>
                {issuesNaoResolvidas.map((issue) => (
                  <div key={issue.id} className="mt-sm" style={{ padding: '0.5rem 0.75rem', background: '#fff', border: '1px solid #f2b8b5', borderRadius: '0.5rem' }}>
                    <span className="font-label-md text-label-md font-bold">{issue.titulo}</span>
                    {issue.descricao && (
                      <p className="font-body-sm text-body-sm text-on-surface-variant m-0">{issue.descricao}</p>
                    )}
                  </div>
                ))}
              </Card>
            )}
          </div>

          {/* Confirmação / assinatura */}
          <div className="lg:col-span-5">
            <Card variant="outlined" padding="lg">
              <h3 className="font-headline-sm text-headline-sm text-on-surface mb-md">Confirmar validação e assinatura</h3>

              {jaAssinado && (
                <div
                  style={{
                    background: '#e8f5e9',
                    border: '1px solid #a5d6a7',
                    color: '#1b5e20',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    marginBottom: '1rem',
                  }}
                >
                  Este período já foi assinado ({formatDateTime(period!.assinadoEm!)}).
                </div>
              )}

              {items.length > 0 && !checklistCompleto && (
                <div
                  style={{
                    background: '#fff8e1',
                    border: '1px solid #ffe082',
                    color: '#8d6e00',
                    borderRadius: '0.5rem',
                    padding: '0.75rem 1rem',
                    fontSize: '0.875rem',
                    marginBottom: '1rem',
                  }}
                >
                  O checklist ainda não está completo ({concluidos}/{items.length} itens). Conclua o workflow antes de assinar.
                </div>
              )}

              <div className="space-y-md">
                <Input
                  label="Assinado por"
                  placeholder="Nome completo"
                  value={assinadoPor}
                  onChange={(e) => setAssinadoPor(e.target.value)}
                />
                <Input
                  label="Cargo do assinante"
                  placeholder="Ex.: Contador responsável"
                  value={cargoAssinante}
                  onChange={(e) => setCargoAssinante(e.target.value)}
                />
                <Textarea
                  label="Nota de validação"
                  placeholder="Observações sobre a validação (opcional)"
                  rows={3}
                  value={notaValidacao}
                  onChange={(e) => setNotaValidacao(e.target.value)}
                />
              </div>

              <Button
                variant="primary"
                size="lg"
                fullWidth
                disabled={bloqueado}
                loading={signMutation.isPending}
                onClick={handleSign}
                style={{ marginTop: '1.25rem' }}
              >
                {jaAssinado ? 'Período já assinado' : signMutation.isPending ? 'Validando...' : 'Validar e assinar'}
              </Button>
              <p className="font-label-sm text-label-sm text-on-surface-variant m-0" style={{ marginTop: '0.75rem', textAlign: 'center' }}>
                A assinatura valida o workflow e fecha o período.
              </p>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}